import { Zap, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DialogFooter } from "../ui/dialog";

const QuickStoryMode = ({
  quickStoryData,
  setQuickStoryData,
  generatingQuickStory,
  quickStoryProgress,
  onGenerateQuickStory,
  onBack,
}) => {
  return (
    <div className="space-y-4">
      {!generatingQuickStory ? (
        <>
          <div className="text-center mb-4">
            <Zap className="w-12 h-12 mx-auto text-emerald-600 mb-2" />
            <h3 className="font-semibold text-lg text-foreground">Create a Short Story</h3>
            <p className="text-sm text-muted-foreground">Generate a complete 1,000-2,000 word story in about a minute</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Story Idea <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={quickStoryData.idea}
                onChange={(e) => setQuickStoryData({...quickStoryData, idea: e.target.value})}
                placeholder="A young inventor discovers a portal in their grandmother's attic that leads to a world where imagination becomes reality..."
                className="min-h-[100px]"
                data-testid="quick-story-idea"
              />
              <p className="text-xs text-muted-foreground mt-1">Describe your story idea in detail. The more detail, the better!</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Title (optional)</label>
                <Input
                  value={quickStoryData.title}
                  onChange={(e) => setQuickStoryData({...quickStoryData, title: e.target.value})}
                  placeholder="Leave blank to auto-generate"
                  data-testid="quick-story-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Genre</label>
                <Select value={quickStoryData.genre} onValueChange={(v) => setQuickStoryData({...quickStoryData, genre: v})}>
                  <SelectTrigger data-testid="quick-story-genre">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="literary">Literary Fiction</SelectItem>
                    <SelectItem value="fantasy">Fantasy</SelectItem>
                    <SelectItem value="scifi">Science Fiction</SelectItem>
                    <SelectItem value="romance">Romance</SelectItem>
                    <SelectItem value="thriller">Thriller</SelectItem>
                    <SelectItem value="horror">Horror</SelectItem>
                    <SelectItem value="mystery">Mystery</SelectItem>
                    <SelectItem value="childrens">Children's</SelectItem>
                    <SelectItem value="comedy">Comedy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Story Length</label>
                <Select value={String(quickStoryData.word_count)} onValueChange={(v) => setQuickStoryData({...quickStoryData, word_count: parseInt(v)})}>
                  <SelectTrigger data-testid="quick-story-length">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1000">~1,000 words</SelectItem>
                    <SelectItem value="1500">~1,500 words</SelectItem>
                    <SelectItem value="2000">~2,000 words</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Audience</label>
                <Select value={quickStoryData.audience_age} onValueChange={(v) => setQuickStoryData({...quickStoryData, audience_age: v})}>
                  <SelectTrigger data-testid="quick-story-audience">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="children">Children (5-10)</SelectItem>
                    <SelectItem value="middle_grade">Middle Grade (10-14)</SelectItem>
                    <SelectItem value="young_adult">Young Adult (14-18)</SelectItem>
                    <SelectItem value="adult">Adult (18+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tone</label>
                <Select value={quickStoryData.tone} onValueChange={(v) => setQuickStoryData({...quickStoryData, tone: v})}>
                  <SelectTrigger data-testid="quick-story-tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="balanced">Balanced</SelectItem>
                    <SelectItem value="dark">Dark & Moody</SelectItem>
                    <SelectItem value="light">Light & Fun</SelectItem>
                    <SelectItem value="dramatic">Dramatic</SelectItem>
                    <SelectItem value="whimsical">Whimsical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-6">
            <Button variant="outline" onClick={onBack}>Back</Button>
            <Button 
              onClick={onGenerateQuickStory}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={quickStoryData.idea.length < 20}
              data-testid="generate-quick-story-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Generate Short Story
            </Button>
          </DialogFooter>
        </>
      ) : (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
          <h3 className="font-semibold text-lg text-foreground mb-2">Writing Your Story...</h3>
          <p className="text-sm text-muted-foreground mb-4">{quickStoryProgress.message || 'Please wait while Shweebum crafts your story'}</p>
          <div className="w-full max-w-xs mx-auto bg-slate-700 rounded-full h-2">
            <div 
              className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
              style={{width: `${(quickStoryProgress.step / quickStoryProgress.total) * 100}%`}}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">Step {quickStoryProgress.step} of {quickStoryProgress.total}</p>
        </div>
      )}
    </div>
  );
};

export default QuickStoryMode;
